import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { toPlural, toSeoUrl } from 'helpers/HelperFunctions';
import { isTierOneOrTwoCity } from 'helpers/LocationTiers';
import { getFirstName } from 'helpers/portfolioLayout';
import WebOnly from 'components/NativeApp/WebOnly';
import styles from './PortfolioExtras.module.scss';

const PHONE_LINKS = 5;

export const HireBand = ({ user, onContact }) => {
  const firstName = getFirstName(user.displayname);

  return (
    <section
      className={styles.hireBand}
      aria-labelledby="portfolio-hire-band-title"
    >
      <div className={styles.hireCopy}>
        <h2 id="portfolio-hire-band-title" className={styles.hireTitle}>
          Hire {user.displayname} today
        </h2>
        <p className={styles.hireText}>
          Post a job and invite {firstName}, or contact them directly. No hidden
          commission.
        </p>
      </div>
      <div className={styles.hireActions}>
        <a
          href={`/post-a-job?freelancer=${user.username}`}
          className={styles.outlineButton}
        >
          Post a job
        </a>
        <button
          type="button"
          className={styles.primaryButton}
          onClick={onContact}
        >
          Contact {firstName}
        </button>
      </div>
    </section>
  );
};

HireBand.propTypes = {
  user: PropTypes.object.isRequired,
  onContact: PropTypes.func.isRequired,
};

const getFindUrl = (role, location) =>
  `/find/${toSeoUrl(toPlural(role))}` +
  (location.country_code !== '' ? '/' + location.country_code : '') +
  (location.country_code !== '' &&
  location.city_slug !== '' &&
  isTierOneOrTwoCity(location.country_code, location.city_slug)
    ? '/' + location.city_slug
    : '');

/** The "for hire in" SEO links that used to sit in the hire box. */
export const MoreExperts = ({ user, showPlanNote }) => {
  const [showAll, setShowAll] = useState(false);
  const roles = [...(user.featured_roles || [])].sort();
  const { location } = user;

  if (roles.length === 0) {
    return null;
  }

  const place = location.city
    ? location.city + (location.country ? ', ' + location.country : '')
    : '';
  const hiddenOnPhone = roles.length - PHONE_LINKS;

  return (
    <section
      className={styles.moreExperts}
      aria-labelledby="portfolio-more-experts-title"
    >
      <h2 id="portfolio-more-experts-title" className={styles.moreTitle}>
        {place ? `More experts in ${place}` : 'More experts'}
      </h2>
      {/* Sells Pro, which the app can't do (Apple 3.1.1). */}
      {showPlanNote && (
        <WebOnly>
          <p className={styles.planNote}>
            Clients see these links because you&apos;re on the free plan.{' '}
            <a href="/pricing/creative">Upgrade to Pro</a> to remove them.
          </p>
        </WebOnly>
      )}
      <ul className={styles.links}>
        {roles.map((role, idx) => (
          <li
            key={role}
            className={
              !showAll && idx >= PHONE_LINKS ? styles.hiddenOnPhone : ''
            }
          >
            <a href={getFindUrl(role, location)}>
              {toPlural(role)} for hire{place ? ` in ${place}` : ''}
            </a>
          </li>
        ))}
      </ul>
      {hiddenOnPhone > 0 && !showAll && (
        <button
          type="button"
          className={styles.moreButton}
          onClick={() => setShowAll(true)}
        >
          {hiddenOnPhone} more
        </button>
      )}
    </section>
  );
};

MoreExperts.propTypes = {
  user: PropTypes.object.isRequired,
  // Shown to a free owner previewing as a client.
  showPlanNote: PropTypes.bool,
};
